import { useState } from "react";
import ArabicToggle from "@/components/ArabicToggle";
import AudioPlayer from "@/components/AudioPlayer";
import SEOHead from "@/components/SEOHead";
import Header from "@/components/Header";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Link } from "wouter";
import { ArrowRight, CheckCircle2, Search, Brain, FileText, BarChart3, Zap, Target, Globe, Shield } from "lucide-react";
import SharePostRow from "@/components/SharePostRow";


import { trackSectionCtaClick, trackCalendlyClick } from "@/lib/analytics";
import SchemaScript from "@/components/SchemaScript";
// ─── Schema & FAQ Data ────────────────────────────────────────────────────────

const faqData = [
  {
    q: "What is Google AI Mode and how is it different from AI Overviews?",
    a: "Google AI Mode is a dedicated AI-powered search experience launched in 2025 that generates multi-step, conversational answers for complex queries. Unlike AI Overviews, which appear as a brief summary above standard results, AI Mode replaces the traditional SERP entirely with an AI-generated response panel, cited sources, and follow-up prompts. For B2B brands, AI Mode is where high-intent research queries now resolve, making citation in AI Mode answers more commercially valuable than a page-one ranking."
  },
  {
    q: "How do you optimize content to appear in Google AI Mode answers?",
    a: "AI Mode citation requires four things: answer-first content structure (direct answer in the first 40-60 words), comprehensive entity coverage (your brand, products, and expertise mapped to the Google Knowledge Graph), structured data that signals document type and authority (NewsArticle, FAQPage, HowTo, Service schemas), and topical depth across a pillar-spoke architecture so Google can verify your authority on the subject. We audit all four dimensions and rebuild content to meet them."
  },
  {
    q: "Does Google AI Mode affect B2B lead generation?",
    a: "Yes, significantly. B2B buyers use AI Mode for complex research queries such as 'best agentic AI platform for mid-market SaaS' or 'how to implement AEO for a B2B brand'. If your brand is not cited in those answers, you are invisible at the highest-intent moment of the research phase. Brands cited in AI Mode answers see 3-5x higher click-through rates than standard organic listings because the citation carries an implicit endorsement from Google's AI."
  },
  {
    q: "What is the difference between AEO and Google AI Mode Optimization?",
    a: "Answer Engine Optimization (AEO) is the broader discipline of structuring content to be cited across all AI answer surfaces: Google AI Overviews, AI Mode, ChatGPT, Perplexity, Gemini, and voice search. Google AI Mode Optimization is a specific subset focused on the signals, schema types, and content formats that Google's AI Mode system prioritizes. We deliver both as part of the same program, with AI Mode receiving dedicated technical attention given its growing share of B2B research queries."
  },
  {
    q: "How long does it take to get cited in Google AI Mode?",
    a: "Initial citations typically appear within 4-8 weeks of implementing the full technical and content stack: answer-first rewrites, schema deployment, entity authority building, and internal linking. The timeline depends on your current domain authority, how much content needs restructuring, and how competitive your target queries are. We track AI Mode citation rates weekly using Google Search Console AI Overview data and manual query testing."
  },
  {
    q: "Do you need a separate strategy for Google AI Mode versus traditional SEO?",
    a: "You need a complementary strategy, not a separate one. Traditional SEO signals (domain authority, backlinks, Core Web Vitals) remain relevant because AI Mode draws from the same index. What changes is the content format: AI Mode rewards answer-first structure, entity-rich copy, and schema-backed authority signals over keyword density and meta tag optimization. We integrate AI Mode optimization into your existing SEO program rather than treating it as a separate workstream."
  },
  {
    q: "Can you optimize existing content for Google AI Mode or does everything need to be rewritten?",
    a: "Most existing content can be retrofitted rather than fully rewritten. The core changes are: adding a direct answer paragraph at the top of each page, restructuring H2/H3 headings as question-answer pairs, implementing FAQPage and HowTo schema, and strengthening entity signals in the copy. A full rewrite is only necessary when the content is fundamentally misaligned with the query intent AI Mode is trying to answer."
  }
];

const deliverables = [
  {
    icon: Search,
    title: "AI Mode Citation Audit",
    desc: "We test 50-100 of your target queries in Google AI Mode, map which competitors are cited, and identify the exact content and schema gaps preventing your brand from appearing."
  },
  {
    icon: FileText,
    title: "Answer-First Content Restructuring",
    desc: "Every priority page is rewritten with a direct answer in the first 40-60 words, question-answer H2/H3 structure, and entity-rich copy that signals topical authority to Google's AI."
  },
  {
    icon: Brain,
    title: "Schema Architecture Deployment",
    desc: "Full implementation of FAQPage, HowTo, NewsArticle, Service, and BreadcrumbList schemas with Speakable markup, injected into static HTML so Googlebot reads them on first crawl."
  },
  {
    icon: Globe,
    title: "Entity Authority Building",
    desc: "We map your brand, products, and expertise to the Google Knowledge Graph using structured data, Wikipedia signals, and authoritative external citations to strengthen entity recognition."
  },
  {
    icon: Target,
    title: "Pillar-Spoke Content Architecture",
    desc: "We build or restructure your content around 5-7 topic pillars with 8-12 spoke articles each, creating the topical depth AI Mode requires to treat your site as a primary source."
  },
  {
    icon: BarChart3,
    title: "AI Mode Citation Tracking",
    desc: "Weekly reporting on AI Mode citation rates, impression share in Google Search Console AI Overview data, and manual query testing across your top 50 commercial keywords."
  }
];

const howItWorks = [
  {
    step: "01",
    title: "AI Mode Gap Analysis",
    desc: "We audit your current citation rate across 50-100 target queries, map competitor citations, and score your content against the 12 AI Mode ranking signals we have identified from testing."
  },
  {
    step: "02",
    title: "Technical Schema Sprint",
    desc: "We deploy the full schema stack (FAQPage, HowTo, NewsArticle, Service, BreadcrumbList, Speakable) into your static HTML within the first two weeks, giving Googlebot immediate access to structured signals."
  },
  {
    step: "03",
    title: "Content Restructuring Program",
    desc: "Priority pages are rewritten with answer-first structure and question-answer headings. New spoke content fills topical gaps identified in the audit. All content is reviewed against E-E-A-T criteria."
  },
  {
    step: "04",
    title: "Measure, Iterate, Scale",
    desc: "We track citation rates weekly, test new queries monthly, and iterate content based on what AI Mode is and is not citing. As citation rates grow, we expand the program to new topic clusters."
  }
];

const outcomes = [
  { metric: "4-8 weeks", label: "to first AI Mode citations after full schema deployment" },
  { metric: "3-5x", label: "higher CTR from AI Mode citations vs standard organic listings" },
  { metric: "60%+", label: "of B2B research queries now resolve in AI Mode or AI Overviews" },
  { metric: "256+", label: "structured Q&A pairs deployed across Integrated.Social - our own proof of concept" }
];

export default function ServiceGoogleAIMode() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://integrated.social/services/google-ai-mode-optimisation/",
        "url": "https://integrated.social/services/google-ai-mode-optimisation/",
        "name": "Google AI Mode Optimisation Agency London | Integrated.Social",
        "description": "We optimise B2B brands to be cited in Google AI Mode answers. Answer-first content, full schema architecture, entity authority building, and weekly citation tracking. London-based, global delivery.",
        "isPartOf": { "@id": "https://integrated.social/#website" },
        "about": { "@id": "https://integrated.social/services/google-ai-mode-optimisation/#service" },
        "author": { "@id": "https://integrated.social/modi-elnadi/#person" },
        "inLanguage": "en-GB"
      },
      {
        "@type": "Service",
        "@id": "https://integrated.social/services/google-ai-mode-optimisation/#service",
        "name": "Google AI Mode Optimisation",
        "provider": { "@id": "https://integrated.social/#organization" },
        "serviceType": "Google AI Mode Optimization & Answer Engine Optimization",
        "description": "We optimise B2B brands to be cited in Google AI Mode answers through answer-first content restructuring, comprehensive schema deployment, entity authority building, and pillar-spoke content architecture.",
        "areaServed": [
          { "@type": "Country", "name": "United Kingdom" },
          { "@type": "Country", "name": "United States" },
          { "@type": "Place", "name": "Global" }
        ],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Google AI Mode Optimisation Services",
          "itemListElement": [
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "AI Mode Citation Audit" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Answer-First Content Restructuring" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Schema Architecture Deployment" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Entity Authority Building" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Pillar-Spoke Content Architecture" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "AI Mode Citation Tracking" } }
          ]
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqData.map(({ q, a }) => ({
          "@type": "Question",
          "name": q,
          "acceptedAnswer": { "@type": "Answer", "text": a }
        }))
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://integrated.social/" },
          { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://integrated.social/services/" },
          { "@type": "ListItem", "position": 3, "name": "Google AI Mode Optimisation", "item": "https://integrated.social/services/google-ai-mode-optimisation/" }
        ]
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="Google AI Mode Agency London | B2B AI Citation Strategy & Search Visibility"
        description="Get your B2B brand cited in Google AI Mode answers. Structured content, entity authority, and schema strategy that puts your brand in AI-generated search results for high-intent queries."
        canonical="https://integrated.social/services/google-ai-mode-optimisation/"
        ogImage="https://integrated.social/media/hero-google-ai-mode-optimisation_4a9ebc6c.jpg"
      />
    <div className="min-h-screen bg-background text-foreground">
      <SchemaScript schema={JSON.stringify(schemaData)} />

      <Header />
      <main>
      <div className="container pt-20 pb-0">
        <Breadcrumbs
          includeSchema={false}
          items={[
            { label: "Services", href: "/services/" },
            { label: "Google AI Mode Optimisation" },
          ]}
        />
      </div>

      {/* Hero */}
      <header className="container py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="max-w-4xl">
            <span className="inline-block px-3 py-1 text-xs font-mono uppercase tracking-widest text-[#FF8C00] border border-[#FF8C00]/30 rounded-full mb-6">
              Google AI Mode Optimization
            </span>
            <h1 className="font-serif headline-xl mb-6">
              Your buyers are asking Google AI Mode. Are you in the answer?
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed mb-8">
              Google AI Mode is where B2B buyers now research complex purchases. We restructure your content, deploy full schema architecture, and build entity authority so your brand appears as a cited source, not a buried result.
            </p>
            <div className="flex flex-wrap gap-4">
              <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London"
                href="https://calendly.com/modielnadi/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF8C00] text-white font-medium rounded-lg hover:bg-[#E07B00] transition-all duration-200 active:scale-[0.97]" onClick={() => { trackCalendlyClick("service_google_ai_mode"); trackSectionCtaClick({ section_name: "service_google_ai_mode_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}
              >
                Book your AI Mode Audit <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                href="/blog/google-ai-mode-vs-ai-overviews-b2b-marketers-2026/"
                className="inline-flex items-center gap-2 px-6 py-3 border border-border text-foreground font-medium rounded-lg hover:bg-accent transition-all duration-200"
              >
                Read: AI Mode vs AI Overviews
              </Link>
            </div>
          </div>
          <div className="hidden lg:block">
            <img
              src="/media/hero-google-ai-mode-optimisation_4a9ebc6c.jpg"
              alt="Google AI Mode optimization for B2B brands: citation card strategy, answer-first content architecture, and structured data implementation to get cited in Google AI Overviews and AI Mode"
              className="w-full h-auto rounded-xl"
              width={600}
              height={400}
              loading="eager"
            />
          </div>
        </div>
      </header>

      {/* Why AI Mode matters */}
      <section className="bg-[#1a1a1a] text-white py-20">
        <div className="container">
          <h2 className="font-serif headline-lg mb-4">Why AI Mode changes B2B search</h2>
          <p className="text-gray-400 text-lg max-w-2xl mb-12">
            AI Mode does not just summarize results. It replaces the SERP for complex queries with an AI-generated answer panel. If you are not cited, you do not exist at the most important moment in the buyer journey.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Zap, title: "60%+ of B2B research queries now resolve in AI Mode", desc: "Complex, multi-step research queries, the kind B2B buyers use to evaluate vendors, are increasingly answered by AI Mode rather than traditional results pages." },
              { icon: Target, title: "Citations carry implicit Google endorsement", desc: "Being cited in an AI Mode answer signals to the buyer that Google's AI considers your content authoritative. This trust transfer drives 3-5x higher CTR than standard organic listings." },
              { icon: Shield, title: "Early movers build citation moats", desc: "AI Mode citation is self-reinforcing: cited sources get more crawls, more entity signals, and higher citation rates over time. Brands that establish citation authority now will be significantly harder to displace in 12 months." }
            ].map((item, i) => (
              <div key={i} className="p-6 border border-white/10 rounded-lg hover:border-[#FF8C00]/40 transition-colors">
                <item.icon className="w-8 h-8 text-[#FF8C00] mb-4" />
                <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                <p className="text-gray-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
        {/* Audio Narrative - voice summary of this service page */}
        <div className="container max-w-4xl mx-auto px-4 py-6">
          <AudioPlayer
            text="Google AI Mode Agency for B2B Brands. We optimize B2B brands to be cited in Google AI Mode answers. Answer-first content architecture, FAQPage schema, entity authority building, and passage retrieval optimization. Google AI Mode is the new battleground for B2B visibility. The brands that appear in AI Mode answers get the attention that used to go to page one organic results."
            title="Google AI Mode Agency | B2B Citation Strategy"
            readingTime={5}
          />
        </div>

      {/* What we deliver */}
      <section className="py-20">
        <div className="container">
          <h2 className="font-serif headline-lg mb-4">What we deliver</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mb-12">
            Six interconnected workstreams that build the technical and content foundation for sustained AI Mode citation.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {deliverables.map((item, i) => (
              <div key={i} className="p-6 border border-border rounded-lg hover:border-[#FF8C00]/40 transition-colors">
                <item.icon className="w-8 h-8 text-[#FF8C00] mb-4" />
                <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Mode vs Traditional SEO comparison */}
      <section className="py-20 bg-accent/50">
        <div className="container">
          <h2 className="font-serif headline-lg mb-12">AI Mode vs traditional SEO: what changes</h2>
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-xl font-semibold mb-4 text-red-500">Traditional SEO signals (still relevant)</h3>
              <ul className="space-y-3">
                {[
                  "Domain authority and backlink profile",
                  "Core Web Vitals and page speed",
                  "Keyword density and meta tags",
                  "Internal linking structure",
                  "Mobile-first indexing"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-muted-foreground">
                    <CheckCircle2 className="w-5 h-5 text-muted-foreground/50 mt-0.5 shrink-0" /> {item}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4 text-[#FF8C00]">AI Mode-specific signals (what we add)</h3>
              <ul className="space-y-3">
                {[
                  "Answer-first content structure (direct answer in first 40-60 words)",
                  "FAQPage, HowTo, and NewsArticle schema in static HTML",
                  "Entity authority mapped to Google Knowledge Graph",
                  "Topical depth across pillar-spoke architecture",
                  "Speakable markup for AI answer extraction",
                  "E-E-A-T signals: author credentials, editorial policy, citations"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-foreground">
                    <CheckCircle2 className="w-5 h-5 text-[#FF8C00] mt-0.5 shrink-0" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20">
        <div className="container">
          <h2 className="font-serif headline-lg mb-12">How we build your AI Mode presence</h2>
          <div className="grid md:grid-cols-4 gap-8">
            {howItWorks.map((item, i) => (
              <div key={i} className="relative">
                <span className="text-6xl font-serif text-[#FF8C00]/20">{item.step}</span>
                <h3 className="text-lg font-semibold mt-2 mb-3">{item.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="py-20 bg-[#1a1a1a] text-white">
        <div className="container">
          <h2 className="font-serif headline-lg mb-12">Typical outcomes</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {outcomes.map((item, i) => (
              <div key={i} className="border-l-2 border-[#FF8C00] pl-6">
                <div className="text-4xl font-serif text-[#FF8C00] mb-2">{item.metric}</div>
                <p className="text-gray-400 leading-relaxed">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="container max-w-3xl">
          <h2 className="font-serif headline-lg mb-12">Frequently asked questions</h2>
          <div className="space-y-4">
            {faqData.map((item, i) => (
              <div key={i} className="border border-border rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full text-left px-6 py-5 flex items-center justify-between hover:bg-accent/50 transition-colors"
                  aria-expanded={openFaq === i}
                >
                  <span className="font-medium pr-4">{item.q}</span>
                  <span className="text-[#FF8C00] text-xl shrink-0">{openFaq === i ? "−" : "+"}</span>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-muted-foreground leading-relaxed">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related content */}
      <section className="py-20 bg-accent/30">
        <div className="container">
          <h2 className="font-serif text-3xl md:text-4xl mb-8">Related reading</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                href: "/blog/google-ai-mode-vs-ai-overviews-b2b-marketers-2026/",
                label: "Google AI Mode vs AI Overviews",
                desc: "The structural differences B2B marketers need to understand to optimize for both surfaces."
              },
              {
                href: "/blog/write-content-cited-chatgpt-answers/",
                label: "How to Write Content Cited in AI Answers",
                desc: "The answer-first content framework that works across Google AI Mode, ChatGPT, and Perplexity."
              },
              {
                href: "/services/seo-aeo-geo/",
                label: "SEO + AEO/GEO Service",
                desc: "Our full AI search visibility program, covering all answer engines alongside traditional SEO."
              }
            ].map((item, i) => (
              <Link
                key={i}
                href={item.href}
                className="block p-6 border border-border rounded-lg hover:border-[#FF8C00]/40 transition-colors group"
              >
                <h3 className="font-semibold mb-2 group-hover:text-[#FF8C00] transition-colors">{item.label}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container text-center max-w-2xl">
          <h2 className="font-serif headline-lg mb-6">
            Start appearing in Google AI Mode answers
          </h2>
          <p className="text-muted-foreground text-lg mb-8">
            Book a free AI Mode audit. We will test 20 of your target queries, show you where competitors are being cited, and outline the exact steps to build your citation presence.
          </p>
          <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London"
            href="https://calendly.com/modielnadi/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#FF8C00] text-white font-medium rounded-lg hover:bg-[#E07B00] transition-all duration-200 active:scale-[0.97] text-lg" onClick={() => { trackCalendlyClick("service_google_ai_mode"); trackSectionCtaClick({ section_name: "service_google_ai_mode_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}
          >
            Book your free AI Mode audit <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

        {/* Final CTA - three ways to work with us */}
        <section id="get-started" className="py-20 lg:py-28 bg-[#0F1419]" aria-labelledby="service-final-cta-heading">
          <div className="container">
            <div className="text-center mb-14">
              <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">Ready to start?</span>
              <h2 id="service-final-cta-heading" className="font-serif text-3xl md:text-4xl text-white mb-4">
                Three ways to work with us
              </h2>
              <p className="text-white/50 text-lg max-w-xl mx-auto">
                Pick the path that fits where you are right now.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">Free · No obligation</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">Audit your domain for AI Answers</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  See exactly where your brand appears in Google AI Mode, ChatGPT, Perplexity, and Gemini. Free AI Visibility Score with a scored report and gap analysis.
                </p>
                <a aria-label="Check my AI visibility"
                  href="/free-ai-growth-audit/"
                  className="inline-flex items-center justify-center gap-2 bg-[#FF8C00] text-[#0F1419] px-6 py-3.5 text-sm font-bold rounded-sm hover:bg-[#E07B00] transition-colors active:scale-[0.97]" onClick={() => trackSectionCtaClick({ section_name: "service_google_ai_mode_bottom_cta", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })}
                >
                  Check my AI visibility<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">1-to-1 · Personalised</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">AI Executive Training for Leaders</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  Three sessions built around your role, your sector, and your goals. Hands-on AI training that makes you dangerous with the tools your competitors are still figuring out.
                </p>
                <a
                  href="/services/ai-training-for-leaders/"
                  className="inline-flex items-center justify-center gap-2 border border-[#FF8C00] text-[#FF8C00] px-6 py-3.5 text-sm font-bold rounded-sm hover:bg-[#FF8C00] hover:text-[#0F1419] transition-colors active:scale-[0.97]"
                >
                  Explore AI Training
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">30 min · No pitch</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">Discuss your AI marketing strategy</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  A focused conversation about where you are, where you want to be, and what a realistic 90-day AI marketing deployment looks like for your business. No deck. No sales script.
                </p>
                <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London"
                  href="https://calendly.com/modielnadi/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border border-white/20 text-white/80 px-6 py-3.5 text-sm font-bold rounded-sm hover:border-[#FF8C00] hover:text-[#FF8C00] transition-colors active:scale-[0.97]" onClick={() => { trackCalendlyClick("service_google_ai_mode"); trackSectionCtaClick({ section_name: "service_google_ai_mode_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}
                >
                  Schedule a discovery call
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
      {/* Arabic-aware share row */}
      <div className="container max-w-5xl mx-auto px-4 pb-16">
        <SharePostRow
          title="Google AI Mode Optimization"
          canonicalUrl="https://integrated.social/services/google-ai-mode-optimisation/"
          excerpt="Get your brand cited in Google AI Mode answers so buyers find you before they find your competitors."
        />
      </div>

      {/* Arabic translation toggle */}
      <ArabicToggle />
    </>
  );
}
